import type { MultiPolygon, Polygon, Position } from 'geojson';

import { createTtCache, type TtCache } from './ttCache';
import type { LatLng, TravelMode } from './walkshed';

/**
 * Real travel times from TravelTime (contract §17), called from the browser:
 * no proxy for the demo (Sam), and TravelTime answers CORS with `*`.
 *
 * Two calls. `timeMap` is the isochrone the map draws: everywhere reachable
 * within N minutes. `timeFilter` is the truth the list uses: minutes from the
 * origin to every event's point, in one request per origin and mode, whatever
 * the slider says. Both are cached for a day (`ttCache.ts`), keyed by the
 * origin rounded to 4 decimals (about 10 m), which is also what is sent, so a
 * cached answer is exactly the answer the request would get.
 *
 * Credentials come from `VITE_TRAVELTIME_APP_ID` and `VITE_TRAVELTIME_API_KEY`
 * (gitignored `.env.local`, and the host's build variables). Without them the
 * client says `available: false` and the app stays on the geometric walkshed.
 */

const API = 'https://api.traveltimeapp.com/v4';

/** TravelTime's name for each mode. */
export const TRANSPORT: Record<TravelMode, string> = {
  walk: 'walking',
  bike: 'cycling',
  transit: 'public_transport',
};

/** How far `timeFilter` looks: the slider's last step. Anything further is `null`. */
export const FILTER_REACH_MIN = 90;
/** TravelTime takes at most 2,000 locations a request, the origin among them. */
const MAX_LOCATIONS = 2000;

/** An event's point, named so the answer can be matched back. */
export interface TimePoint {
  id: string;
  point: LatLng;
}

export interface TravelTimeClient {
  /** False without credentials: every caller falls back at once. */
  available: boolean;
  timeMap(origin: LatLng, mode: TravelMode, minutes: number): Promise<MultiPolygon>;
  /** Minutes to each point, rounded up; null where it can't be reached within 90. */
  timeFilter(origin: LatLng, mode: TravelMode, points: TimePoint[]): Promise<Map<string, number | null>>;
  /** The cached answers, synchronously, so a reload draws without waiting. */
  peekTimeMap(origin: LatLng, mode: TravelMode, minutes: number): MultiPolygon | undefined;
  peekTimeFilter(origin: LatLng, mode: TravelMode, points: TimePoint[]): Map<string, number | null> | undefined;
}

// --- request shapes ----------------------------------------------------------------

/** 4 decimals: the cache's grain, and what is sent. */
export const round4 = (n: number) => Math.round(n * 1e4) / 1e4;
const coords = ([lat, lng]: LatLng) => ({ lat: round4(lat), lng: round4(lng) });
const originKey = (o: LatLng) => `${round4(o[0])},${round4(o[1])}`;

/**
 * The next quarter hour, so transit is routed on a real timetable rather than
 * "now" to the second. Every timezone's offset is a whole number of quarter
 * hours, so a quarter hour in UTC is one on the city's clock too.
 */
export function nextQuarterHour(now: number = Date.now()): string {
  const q = 15 * 60 * 1000;
  return new Date(Math.ceil(now / q) * q).toISOString();
}

/** A stable signature of a set of points, for the cache key. */
function pointsSignature(points: TimePoint[]): string {
  return points
    .map((p) => `${p.id}@${p.point[0].toFixed(5)},${p.point[1].toFixed(5)}`)
    .sort()
    .join(';');
}

const mapKey = (origin: LatLng, mode: TravelMode, minutes: number) => `map|${originKey(origin)}|${mode}|${minutes}`;
const filterKey = (origin: LatLng, mode: TravelMode, points: TimePoint[]) =>
  `filter|${originKey(origin)}|${mode}|${pointsSignature(points)}`;

// --- responses ---------------------------------------------------------------------

interface ApiCoord {
  lat: number;
  lng: number;
}

export interface TimeMapResponse {
  results: { search_id: string; shapes: { shell: ApiCoord[]; holes: ApiCoord[][] }[] }[];
}

export interface TimeFilterResponse {
  results: {
    search_id: string;
    locations: { id: string; properties: { travel_time: number }[] }[];
    unreachable: string[];
  }[];
}

/** 5 decimals is about a metre: plenty for a drawn edge, and half the storage. */
const r5 = (n: number) => Math.round(n * 1e5) / 1e5;

/** A GeoJSON ring is closed and [lng, lat]. */
function ring(points: ApiCoord[]): Position[] {
  const out = points.map((p) => [r5(p.lng), r5(p.lat)]);
  const [first, last] = [out[0], out.at(-1)];
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) out.push([...first]);
  return out;
}

/** `results[0].shapes[]` as one MultiPolygon: each shape's shell, then its holes. */
export function shapesToMultiPolygon(response: TimeMapResponse): MultiPolygon {
  const shapes = response.results[0]?.shapes ?? [];
  return {
    type: 'MultiPolygon',
    coordinates: shapes.filter((s) => s.shell.length >= 3).map((s) => [ring(s.shell), ...s.holes.filter((h) => h.length >= 3).map(ring)]),
  };
}

/** Seconds → whole minutes, rounded up, as the geometric version rounds. */
export function filterMinutes(response: TimeFilterResponse): Map<string, number | null> {
  const out = new Map<string, number | null>();
  for (const result of response.results) {
    for (const loc of result.locations) {
      const seconds = loc.properties[0]?.travel_time;
      out.set(loc.id, typeof seconds === 'number' ? Math.ceil(seconds / 60) : null);
    }
    for (const id of result.unreachable) out.set(id, null);
  }
  return out;
}

// --- geometry ----------------------------------------------------------------------

/** Even-odd ray cast on one ring of [lng, lat]. */
function inRing([lat, lng]: LatLng, r: Position[]): boolean {
  let inside = false;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
    const [xi, yi] = r[i];
    const [xj, yj] = r[j];
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Inside a shell of some polygon and in none of its holes. */
export function pointInShape(point: LatLng, shape: MultiPolygon | Polygon): boolean {
  const polygons = shape.type === 'Polygon' ? [shape.coordinates] : shape.coordinates;
  return polygons.some(([shell, ...holes]) => shell && inRing(point, shell) && !holes.some((h) => inRing(point, h)));
}

// --- the client ----------------------------------------------------------------------

interface ClientOptions {
  appId?: string;
  apiKey?: string;
  fetch?: typeof fetch;
  cache?: TtCache;
  now?: () => number;
}

export function createTravelTimeClient({
  appId,
  apiKey,
  fetch: fetchImpl = (...args) => fetch(...args),
  cache = createTtCache(),
  now = Date.now,
}: ClientOptions): TravelTimeClient {
  const available = Boolean(appId && apiKey);
  /** One request per key at a time: StrictMode, and two scopes asking at once. */
  const inFlight = new Map<string, Promise<unknown>>();

  const post = async <T>(path: string, body: unknown): Promise<T> => {
    if (!available) throw new Error('TravelTime credentials are not set');
    const res = await fetchImpl(`${API}/${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Application-Id': appId!,
        'X-Api-Key': apiKey!,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`TravelTime ${path} answered ${res.status}`);
    return (await res.json()) as T;
  };

  const once = <T>(key: string, run: () => Promise<T>): Promise<T> => {
    const cached = cache.get<T>(key);
    if (cached !== undefined) return Promise.resolve(cached);
    let p = inFlight.get(key) as Promise<T> | undefined;
    if (!p) {
      p = run()
        .then((value) => {
          cache.set(key, value);
          return value;
        })
        .finally(() => inFlight.delete(key));
      inFlight.set(key, p);
    }
    return p;
  };

  const timeMap = (origin: LatLng, mode: TravelMode, minutes: number) =>
    once(mapKey(origin, mode, minutes), async () =>
      shapesToMultiPolygon(
        await post<TimeMapResponse>('time-map', {
          departure_searches: [
            {
              id: 'origin',
              coords: coords(origin),
              transportation: { type: TRANSPORT[mode] },
              departure_time: nextQuarterHour(now()),
              travel_time: minutes * 60,
            },
          ],
        }),
      ),
    );

  const timeFilter = async (origin: LatLng, mode: TravelMode, points: TimePoint[]) => {
    const entries = await once(filterKey(origin, mode, points), async () => {
      const out = new Map<string, number | null>();
      // The origin takes one of each request's 2,000 places.
      for (let i = 0; i < points.length; i += MAX_LOCATIONS - 1) {
        const chunk = points.slice(i, i + MAX_LOCATIONS - 1);
        const answer = await post<TimeFilterResponse>('time-filter', {
          locations: [{ id: '@origin', coords: coords(origin) }, ...chunk.map((p) => ({ id: p.id, coords: coords(p.point) }))],
          departure_searches: [
            {
              id: 'origin',
              departure_location_id: '@origin',
              arrival_location_ids: chunk.map((p) => p.id),
              transportation: { type: TRANSPORT[mode] },
              departure_time: nextQuarterHour(now()),
              travel_time: FILTER_REACH_MIN * 60,
              properties: ['travel_time'],
            },
          ],
        });
        for (const [id, m] of filterMinutes(answer)) out.set(id, m);
      }
      // Stored as pairs: a Map doesn't survive JSON.
      return [...out];
    });
    return new Map(entries);
  };

  return {
    available,
    timeMap,
    timeFilter,
    peekTimeMap: (origin, mode, minutes) => (available ? cache.get<MultiPolygon>(mapKey(origin, mode, minutes)) : undefined),
    peekTimeFilter: (origin, mode, points) => {
      if (!available) return undefined;
      const entries = cache.get<[string, number | null][]>(filterKey(origin, mode, points));
      return entries ? new Map(entries) : undefined;
    },
  };
}

/** The app's client, on the build's credentials. */
export const traveltime = createTravelTimeClient({
  appId: import.meta.env.VITE_TRAVELTIME_APP_ID,
  apiKey: import.meta.env.VITE_TRAVELTIME_API_KEY,
});
